import { Component, OnInit, OnDestroy } from "@angular/core";
import { SeoService } from "../../../services/seo.service";
import emailjs from "@emailjs/browser";

interface ContactFormModel {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

@Component({
    selector: "app-contact",
    templateUrl: "./contact.component.html",
    styleUrls: ["./contact.component.scss"],
})
export class ContactComponent implements OnInit, OnDestroy {
    contactFormModel: ContactFormModel = {
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    };

    isSubmitting = false;
    submitSuccess = false;
    submitError = false;
    errorMessage = "";
    formSubmitted = false;
    turnstileToken: string = "";
    turnstileVerified = false;
    private readonly emailJsServiceId = "service_x50wc9s";
    private readonly emailJsTemplateId = "template_s2z31nb";
    private readonly emailJsPublicKey = "RtOikXpefpzhIllpX";

    constructor(private seoService: SeoService) {}

    ngOnInit(): void {
        this.seoService.setSEOTags(
            "Contact Us - Insightful IT Services",
            "Get in touch with Insightful IT Services for cloud and managed IT solutions. Our experts are ready to help with your infrastructure needs. 24/7 support available.",
            "Contact IT Services India, Cloud Support India, Managed IT Support, IT Consultation, Enterprise IT Services Contact, Cloud Infrastructure Support"
        );

        setTimeout(() => {
            this.initializeTurnstile();
        }, 100);

        window.addEventListener('turnstileToken', (event: any) => {
            this.turnstileToken = event.detail;
            this.turnstileVerified = true;
            if (this.errorMessage.includes('security verification')) {
                this.submitError = false;
                this.errorMessage = '';
            }
        });
    }

    onSubmit(): void {
        if (this.isSubmitting) {
            return;
        }

        this.submitError = false;
        this.errorMessage = "";

        this.hideAllErrors();

        this.formSubmitted = true;

        let hasErrors = false;

        if (!this.contactFormModel.name) {
            hasErrors = true;
            this.showError("name-error", "Name is required");
        }

        if (!this.contactFormModel.email) {
            hasErrors = true;
            this.showError("email-error", "Email is required");
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.contactFormModel.email)) {
                hasErrors = true;
                this.showError(
                    "email-error",
                    "Please enter a valid email address"
                );
            }
        }

        if (
            !this.contactFormModel.subject ||
            this.contactFormModel.subject.trim() === ""
        ) {
            hasErrors = true;
            this.showError("subject-error", "Subject is required");
        }

        if (!this.contactFormModel.message) {
            hasErrors = true;
            this.showError("message-error", "Message is required");
        }

        if (
            !this.contactFormModel.phone ||
            this.contactFormModel.phone === ""
        ) {
            hasErrors = true;
            this.showError("phone-error", "Phone number is required");
        }

        if (hasErrors) {
            setTimeout(() => {
                const firstError =
                    document.querySelector(".error-message:visible") ||
                    document.querySelector(".error-message");
                if (firstError) {
                    firstError.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 100);
            return;
        }

        if (!this.turnstileToken) {
            this.submitError = true;
            this.errorMessage = "Please complete the security verification (Turnstile) before submitting the form.";
            this.isSubmitting = false;
            
            this.forceTurnstileReinitialize();
            
            return;
        }

        this.isSubmitting = true;
        this.submitSuccess = false;

        const templateParams = {
            name: this.contactFormModel.name,
            email: this.contactFormModel.email,
            phone: this.contactFormModel.phone,
            subject: this.contactFormModel.subject,
            message: this.contactFormModel.message,
            time: new Date().toLocaleString()
        };

        emailjs
            .send(this.emailJsServiceId, this.emailJsTemplateId, templateParams, this.emailJsPublicKey)
            .then(() => {
                this.submitSuccess = true;
                this.resetForm();
                this.turnstileToken = "";
                this.turnstileVerified = false;
                if ((window as any).turnstile) {
                    (window as any).turnstile.reset();
                }
                this.isSubmitting = false;
            })
            .catch(() => {
                this.submitError = true;
                this.errorMessage =
                    "An error occurred while sending your message. Please try again later or contact us directly at ashwini@insightfulitservices.com";
                this.turnstileToken = "";
                this.turnstileVerified = false;
                this.isSubmitting = false;
            });
    }

    resetForm(): void {
        this.contactFormModel = {
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
        };
        this.formSubmitted = false;
        this.submitError = false;
        this.errorMessage = "";
        // Hide all error messages
        this.hideAllErrors();
        // Force validation to recheck
        setTimeout(() => {
            // This will trigger change detection
        }, 0);
    }

    // Hide all error messages
    hideAllErrors(): void {
        const errorElements = document.querySelectorAll(".error-message");
        errorElements.forEach((element) => {
            (element as HTMLElement).style.display = "none";
        });
    }

    // Show specific error message
    showError(elementId: string, message: string): void {
        const errorElement = document.getElementById(elementId);
        if (errorElement) {
            errorElement.textContent = message;
            errorElement.style.display = "block";
        }
    }

    // Check if subject is invalid
    isSubjectInvalid(): boolean {
        // Only show error if form was submitted and subject is empty
        return (
            this.formSubmitted &&
            (!this.contactFormModel.subject ||
                this.contactFormModel.subject === "")
        );
    }

    // Debug method to check form values
    debugForm(): void {
        console.log("Form Values:", this.contactFormModel);
        console.log("Subject Value:", this.contactFormModel.subject);
        console.log(
            "Subject Length:",
            this.contactFormModel.subject
                ? this.contactFormModel.subject.length
                : 0
        );
        console.log(
            "Subject Trimmed:",
            this.contactFormModel.subject
                ? this.contactFormModel.subject.trim()
                : ""
        );
    }

    // Method to handle field changes
    onFieldChange(): void {
        // This method will trigger change detection
        // It's used to ensure validation errors are updated in real-time
        // If the form has been submitted, we check if we should hide any error messages
        if (this.formSubmitted) {
            this.hideFixedErrors();
        }
    }

    // Hide error messages for fields that are now valid
    hideFixedErrors(): void {
        // Check each field and hide its error if it's now valid
        if (
            this.contactFormModel.name &&
            this.contactFormModel.name.trim() !== ""
        ) {
            this.hideError("name-error");
        }

        if (
            this.contactFormModel.email &&
            this.contactFormModel.email.trim() !== ""
        ) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(this.contactFormModel.email)) {
                this.hideError("email-error");
            }
        }

        if (
            this.contactFormModel.phone &&
            this.contactFormModel.phone.trim() !== ""
        ) {
            this.hideError("phone-error");
        }

        if (
            this.contactFormModel.subject &&
            this.contactFormModel.subject.trim() !== ""
        ) {
            this.hideError("subject-error");
        }

        if (
            this.contactFormModel.message &&
            this.contactFormModel.message.trim() !== ""
        ) {
            this.hideError("message-error");
        }
    }

    // Hide specific error message
    hideError(elementId: string): void {
        const errorElement = document.getElementById(elementId);
        if (errorElement) {
            errorElement.style.display = "none";
        }
    }

    toggleAccordion(event: any): void {
        const button = event.target.closest(".accordion-button");
        if (button) {
            const item = button.closest(".accordion-item");
            item.classList.toggle("active");
        }
    }

    private turnstileInitialized = false;
    
    initializeTurnstile(): void {
        // Prevent duplicate initialization
        if (this.turnstileInitialized) {
            return;
        }
        
        // Check if Turnstile script is loaded
        if ((window as any).turnstile) {
            // Wait for the DOM to be fully loaded
            setTimeout(() => {
                // Render the Turnstile widget
                const turnstileContainer = document.getElementById('cf-turnstile-container');
                if (turnstileContainer) {
                    // Remove any existing Turnstile elements
                    const existingTurnstile = turnstileContainer.querySelector('iframe[src*="challenges.cloudflare"]');
                    if (existingTurnstile) {
                        existingTurnstile.remove();
                    }
                    
                    try {
                        // Initialize the widget
                        (window as any).turnstile.render('#cf-turnstile-container', {
                            sitekey: '0x4AAAAAACKDMu5GCxu8ofzB',
                            callback: (token: string) => {
                                // Dispatch custom event to notify Angular
                                window.dispatchEvent(new CustomEvent('turnstileToken', { detail: token }));
                                this.turnstileVerified = true;
                                this.turnstileToken = token;
                            },
                            'expired-callback': () => {
                                // Reset token when expired
                                this.turnstileToken = '';
                                this.turnstileVerified = false;
                            },
                            'error-callback': () => {
                                // Reset token on error
                                this.turnstileToken = '';
                                this.turnstileVerified = false;
                            }
                        });
                        
                        // Mark as initialized
                        this.turnstileInitialized = true;
                    } catch (e) {
                        console.error('Error initializing Turnstile:', e);
                        // Reset the flag to allow re-initialization
                        this.turnstileInitialized = false;
                    }
                }
            }, 100);
        } else {
            // If script is not loaded yet, try again after a delay
            setTimeout(() => {
                this.initializeTurnstile();
            }, 500);
        }
    }

    ngOnDestroy(): void {
        // Clean up any existing Turnstile widget when component is destroyed
        if ((window as any).turnstile) {
            try {
                // Try to reset the widget
                (window as any).turnstile.reset();
            } catch (e) {
                console.log('Turnstile reset failed - likely not initialized');
            }
        }
        
        // Reset initialization flag
        this.turnstileInitialized = false;
    }

    forceTurnstileReinitialize(): void {
        // Reset the initialization flag to allow reinitialization
        this.turnstileInitialized = false;
        
        // Clean up any existing Turnstile widget
        if ((window as any).turnstile) {
            try {
                (window as any).turnstile.reset();
            } catch (e) {
                console.log('Turnstile reset failed during reinitialization');
            }
        }
        
        // Remove any existing Turnstile iframes
        const turnstileContainer = document.getElementById('cf-turnstile-container');
        if (turnstileContainer) {
            const existingTurnstile = turnstileContainer.querySelector('iframe[src*="challenges.cloudflare"]');
            if (existingTurnstile) {
                existingTurnstile.remove();
            }
        }
        
        // Reinitialize after a short delay
        setTimeout(() => {
            this.initializeTurnstile();
        }, 100);
    }
}
